import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, SelectChangeEvent } from '@mui/material';
import { CSVExportParams, getNurses, getNursesCSVPreview } from '../api/nurses';
import {
  AssignStatus,
  BlockedStatus,
  Nurse,
  NurseSearchParams,
  ResignedStatus,
  SeniorTalkRoomStatus,
  TalkStatus,
  TestAccountStatus
} from '../types/nurse';
import { CSVExportDialog } from '../components/common';
import { useCSVExport } from '../hooks/useCSVExport';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
// コンポーネントを導入
import { ModeTabs, NurseSearchForm, NurseTable } from '../components/nurse';
// ローカルストレージのキー定義
const STORAGE_KEY = 'nurse_list_search';
// 看護師一覧ページ
const NurseList: React.FC = () => {
  // 認証情報を取得
  const { user } = useAuth();
  // データ表示用の状態
  const [ nurses, setNurses ] = useState<Nurse[]>([]);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  // リクエスト重複防止用のref
  const requestInProgressRef = useRef<{ [key: string]: boolean }>({});
  // ロケーション情報を取得（ダッシュボードからの遷移パラメータ用）
  const location = useLocation();
  // 検索条件を取得する関数
  const getSavedFilters = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('検索条件の復元に失敗:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  };
  // ダッシュボードからの遷移パラメータがある場合はそれを使用、なければローカルストレージから取得
  const locationState = location.state as {
    roleType?: string[],
    talkStatus?: string,
    resignedStatus?: string,
    assignStatus?: string,
    createdAtStart?: string,
    createdAtEnd?: string,
    workshopStatus?: string[],
    isTrainingMode?: boolean,
    workshopFinishCount?: string,
    workshopFinishCountOperator?: string,
    seniorTalkRoomStatus?: string,
    testAccountStatus?: string
  } | null;
  // ダッシュボードからの遷移時は既存の条件をクリアし、そうでなければ保存された状態を使用
  const savedFilters = locationState ? null : getSavedFilters();
  // 検索フィルター用の状態
  const [ searchTerm, setSearchTerm ] = useState(savedFilters?.searchTerm || '');
  const [ emailSearchTerm, setEmailSearchTerm ] = useState(savedFilters?.emailSearchTerm || '');
  const [ userIdSearchTerm, setUserIdSearchTerm ] = useState(savedFilters?.userIdSearchTerm || '');
  // リストモード状態（0: 通常看護師、1: 研修看護師）
  const [ tabValue, setTabValue ] = useState(locationState?.isTrainingMode ? 1 : (savedFilters?.tabValue || 0));
  // 研修モードの場合はSTAFFロールを設定、それ以外は通常通り
  const [ selectedRoles, setSelectedRoles ] = useState<string[]>(
    locationState?.isTrainingMode && !locationState?.roleType ? [ 'STAFF' ] :
      (locationState?.roleType || savedFilters?.selectedRoles || [])
  );
  // 通常看護師モード用の検索フィルター
  const [ talkStatus, setTalkStatus ] = useState(locationState?.talkStatus || savedFilters?.talkStatus || TalkStatus.ALL);
  const [ assignStatus, setAssignStatus ] = useState(locationState?.assignStatus || savedFilters?.assignStatus || AssignStatus.ALL);
  const [ blockedStatus, setBlockedStatus ] = useState(savedFilters?.blockedStatus || BlockedStatus.ALL);
  // 研修看護師モード用の検索フィルター
  const [ selectedWorkshopStatuses, setSelectedWorkshopStatuses ] = useState<string[]>(locationState?.workshopStatus || savedFilters?.selectedWorkshopStatuses || []);
  const [ workshopFinishCount, setWorkshopFinishCount ] = useState(locationState?.workshopFinishCount || savedFilters?.workshopFinishCount || '');
  const [ workshopFinishCountOperator, setWorkshopFinishCountOperator ] = useState(locationState?.workshopFinishCountOperator || savedFilters?.workshopFinishCountOperator || 'eq'); // eq, gte, lte
  // 通常看護師モード用の追加フィルター
  const [ conversationCount, setConversationCount ] = useState(savedFilters?.conversationCount || '');
  const [ conversationCountOperator, setConversationCountOperator ] = useState(savedFilters?.conversationCountOperator || 'eq'); // eq, gte, lte
  const [ seniorTalkRoomStatus, setSeniorTalkRoomStatus ] = useState(locationState?.seniorTalkRoomStatus || savedFilters?.seniorTalkRoomStatus || SeniorTalkRoomStatus.ALL);
  // 共通フィルター
  const [ resignedStatus, setResignedStatus ] = useState(locationState?.resignedStatus || savedFilters?.resignedStatus || ResignedStatus.ACTIVE_ONLY);
  const [ testAccountStatus, setTestAccountStatus ] = useState<string>(locationState?.testAccountStatus || savedFilters?.testAccountStatus || TestAccountStatus.ALL);
  // 登録日範囲検索用フィルター
  const [ createdAtStart, setCreatedAtStart ] = useState<string | null>(locationState?.createdAtStart || savedFilters?.createdAtStart || null);
  const [ createdAtEnd, setCreatedAtEnd ] = useState<string | null>(locationState?.createdAtEnd || savedFilters?.createdAtEnd || null);
  // ダッシュボードからの遷移時は初期検索パラメータを設定
  const initialSearchParams = locationState ? {
    searchTerm: '',
    emailSearchTerm: '',
    userIdSearchTerm: '',
    // 研修モードの場合はSTAFFロールを設定、それ以外は指定されたロール
    selectedRoles: locationState.isTrainingMode && !locationState.roleType ? [ 'STAFF' ] : (locationState.roleType || []),
    talkStatus: locationState.talkStatus || TalkStatus.ALL,
    assignStatus: locationState.assignStatus || AssignStatus.ALL,
    blockedStatus: BlockedStatus.ALL,
    selectedWorkshopStatuses: locationState.workshopStatus || [] as string[],
    workshopFinishCount: locationState.workshopFinishCount || '',
    workshopFinishCountOperator: locationState.workshopFinishCountOperator || 'eq',
    conversationCount: '',
    conversationCountOperator: 'eq',
    seniorTalkRoomStatus: locationState.seniorTalkRoomStatus || SeniorTalkRoomStatus.ALL,
    resignedStatus: locationState.resignedStatus || ResignedStatus.ACTIVE_ONLY,
    testAccountStatus: locationState.testAccountStatus || TestAccountStatus.ALL,
    createdAtStart: locationState?.createdAtStart || null,
    createdAtEnd: locationState?.createdAtEnd || null
  } : null;
  // 検索条件（検索ボタン押下時に更新）
  const [ searchParams, setSearchParams ] = useState(initialSearchParams || {
    searchTerm: savedFilters?.searchTerm || '',
    emailSearchTerm: savedFilters?.emailSearchTerm || '',
    userIdSearchTerm: savedFilters?.userIdSearchTerm || '',
    selectedRoles: savedFilters?.selectedRoles || [] as string[],
    talkStatus: savedFilters?.talkStatus || TalkStatus.ALL,
    assignStatus: savedFilters?.assignStatus || AssignStatus.ALL,
    blockedStatus: savedFilters?.blockedStatus || BlockedStatus.ALL,
    selectedWorkshopStatuses: savedFilters?.selectedWorkshopStatuses || [] as string[],
    workshopFinishCount: savedFilters?.workshopFinishCount || '',
    workshopFinishCountOperator: savedFilters?.workshopFinishCountOperator || 'eq', // eq, gte, lte
    conversationCount: savedFilters?.conversationCount || '',
    conversationCountOperator: savedFilters?.conversationCountOperator || 'eq', // eq, gte, lte
    seniorTalkRoomStatus: savedFilters?.seniorTalkRoomStatus || SeniorTalkRoomStatus.ALL,
    resignedStatus: savedFilters?.resignedStatus || ResignedStatus.ACTIVE_ONLY,
    testAccountStatus: savedFilters?.testAccountStatus || TestAccountStatus.ALL,
    createdAtStart: savedFilters?.createdAtStart || null,
    createdAtEnd: savedFilters?.createdAtEnd || null
  });
  // ページネーション用の状態
  const [ page, setPage ] = useState(savedFilters?.page || 0);
  const [ rowsPerPage, setRowsPerPage ] = useState(savedFilters?.rowsPerPage || 20);
  // 研修看護師モードかどうか
  const trainingMode = tabValue === 1;
  // CSV出力ダイアログの状態
  const [ csvDialogOpen, setCSVDialogOpen ] = useState(false);
  const [ csvPreviewData, setCSVPreviewData ] = useState<any>(null);
  // CSV出力フック
  const { exportCSV, loading: csvLoading, error: csvError } = useCSVExport({
    endpoint: '/external/nurses',
  });
  // 看護師データを取得する関数
  const fetchNurses = async(params: NurseSearchParams) => {
    // APIパラメータのキャッシュキーを作成
    const cacheKey = JSON.stringify(params);
    // 同じパラメータでのリクエストが進行中であれば、新しいリクエストを送信しない
    if (requestInProgressRef.current[cacheKey]) {
      console.log('同一条件での重複リクエストをスキップします', params);
      return;
    }
    // リクエスト開始をマーク
    requestInProgressRef.current[cacheKey] = true;
    // ローディング状態を設定
    setLoading(true);
    setError(null);
    try {
      // API呼び出し
      const result = await getNurses(params);
      // 結果を状態にセット
      setNurses(result.results || []);
      setTotalCount(result.count || 0);
    } catch (error) {
      console.error('看護師データの取得に失敗:', error);
      setError('看護師データの取得に失敗しました。ネットワーク接続を確認してください。');
      setNurses([]);
      setTotalCount(0);
    } finally {
      // 完了したらローディング状態を解除
      setLoading(false);
      // リクエスト完了をマーク
      delete requestInProgressRef.current[cacheKey];
    }
  };
  // ダッシュボードから遷移してきた場合、localStorageから古い検索条件を削除
  useEffect(() => {
    if (locationState) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [ locationState ]);
  // 検索条件が変更されたらlocalStorageに保存
  useEffect(() => {
    // 検索条件、ページネーション情報、タブ情報をlocalStorageに保存
    const filtersToSave = {
      searchTerm,
      emailSearchTerm,
      userIdSearchTerm,
      selectedRoles,
      tabValue,
      talkStatus,
      assignStatus,
      blockedStatus,
      selectedWorkshopStatuses,
      workshopFinishCount,
      workshopFinishCountOperator,
      conversationCount,
      conversationCountOperator,
      seniorTalkRoomStatus,
      resignedStatus,
      createdAtStart,
      createdAtEnd,
      page,
      rowsPerPage,
      // 検索パラメータも保存
      searchParams
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (e) {
      console.error('検索条件の保存に失敗:', e);
    }
  }, [
    searchParams,
    page,
    rowsPerPage,
    tabValue,
    searchTerm,
    emailSearchTerm,
    userIdSearchTerm,
    selectedRoles,
    talkStatus,
    assignStatus,
    blockedStatus,
    selectedWorkshopStatuses,
    workshopFinishCount,
    workshopFinishCountOperator,
    conversationCount,
    conversationCountOperator,
    seniorTalkRoomStatus,
    resignedStatus,
    testAccountStatus,
    createdAtStart,
    createdAtEnd
  ]);
  // 検索条件変更時にAPIリクエスト
  useEffect(() => {
    // APIリクエスト用のパラメータを作成
    const params: NurseSearchParams = {
      search: searchParams.searchTerm || undefined,
      email_search: searchParams.emailSearchTerm || undefined,
      user_id: searchParams.userIdSearchTerm || undefined,
      page: page + 1, // APIは1から始まるページネーション
      page_size: rowsPerPage
    };
    // ロールフィルターの追加
    if (searchParams.selectedRoles && searchParams.selectedRoles.length > 0) {
      params.role_type = searchParams.selectedRoles;
    }
    // 申込み状態フィルターの追加
    if (searchParams.assignStatus) {
      // 文字列値として渡す必要がある
      if (searchParams.assignStatus === AssignStatus.ASSIGNABLE) {
        params.can_assign = 'true';
      } else if (searchParams.assignStatus === AssignStatus.UNASSIGNABLE) {
        params.can_assign = 'false';
      }
    }
    // 対話状態フィルターの追加
    if (searchParams.talkStatus && searchParams.talkStatus !== TalkStatus.ALL) {
      params.talk_status = searchParams.talkStatus;
    }
    // ブロック状態フィルターの追加
    if (searchParams.blockedStatus && searchParams.blockedStatus !== BlockedStatus.ALL) {
      params.blocked_status = searchParams.blockedStatus;
    }
    // 研修ステータスフィルターの追加
    if (Array.isArray(searchParams.selectedWorkshopStatuses) && searchParams.selectedWorkshopStatuses.length > 0) {
      // 配列をそのまま設定（APIクライアントでURLSearchParamsに変換される）
      params.workshop_status = searchParams.selectedWorkshopStatuses;
    }
    // 研修修了回数フィルターの追加
    if (searchParams.workshopFinishCount && searchParams.workshopFinishCount.trim() !== '') {
      const count = searchParams.workshopFinishCount.trim();
      // 演算子に基づいて適切なパラメータ名を設定
      if (searchParams.workshopFinishCountOperator === 'eq') {
        params.workshop_finish_count = count;
      } else if (searchParams.workshopFinishCountOperator === 'gte') {
        params.workshop_finish_count_gte = count;
      } else if (searchParams.workshopFinishCountOperator === 'lte') {
        params.workshop_finish_count_lte = count;
      }
      // どの演算子が使用されたかも送信（バックエンドでの処理に役立つ可能性がある）
      params.workshop_finish_count_operator = searchParams.workshopFinishCountOperator;
    }
    // 対話回数フィルターの追加
    if (searchParams.conversationCount && searchParams.conversationCount.trim() !== '') {
      const count = searchParams.conversationCount.trim();
      // 演算子に基づいて適切なパラメータ名を設定
      if (searchParams.conversationCountOperator === 'eq') {
        params.conversation_count = count;
      } else if (searchParams.conversationCountOperator === 'gte') {
        params.conversation_count_gte = count;
      } else if (searchParams.conversationCountOperator === 'lte') {
        params.conversation_count_lte = count;
      }
      // どの演算子が使用されたかも送信
      params.conversation_count_operator = searchParams.conversationCountOperator;
    }
    // 上席相談ルーム関係フィルターの追加
    if (searchParams.seniorTalkRoomStatus && searchParams.seniorTalkRoomStatus !== SeniorTalkRoomStatus.ALL) {
      params.senior_talk_room_status = searchParams.seniorTalkRoomStatus;
    }
    // 登録日範囲フィルターの追加 - 患者一覧と同様の仕様
    // 登録日(開始)だけでも検索できる
    if (searchParams.createdAtStart) {
      params.created_at_start = searchParams.createdAtStart;
      // 終了日が設定されている場合のみ追加（終了日単体での検索はない）
      if (searchParams.createdAtEnd) {
        params.created_at_end = searchParams.createdAtEnd;
      }
    }
    // 退会状態フィルターの追加
    if (searchParams.resignedStatus) {
      params.include_resigned = searchParams.resignedStatus;
    }
    // テストアカウントフィルターの追加
    if (searchParams.testAccountStatus && searchParams.testAccountStatus !== TestAccountStatus.ALL) {
      params.is_test_account = searchParams.testAccountStatus;
    }
    // 研修看護師モードフラグの追加
    params.is_training_mode = trainingMode;
    // APIリクエスト実行
    fetchNurses(params);
  }, [ page, rowsPerPage, searchParams, tabValue, trainingMode ]);
  // ページ変更ハンドラ
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  // 1ページあたりの表示件数変更ハンドラ
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    console.log('表示件数変更:', rowsPerPage, '->', newValue);
    setRowsPerPage(newValue);
    setPage(0);
  };
  // フォーム送信ハンドラ
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSearch();
  };
  // 名前/ニックネーム検索フィールドクリアハンドラ
  const handleClearSearchField = () => {
    setSearchTerm('');
    setTimeout(() => {
      handleSearch();
    }, 0);
  };
  // メールアドレス検索フィールドクリアハンドラ
  const handleClearEmailField = () => {
    setEmailSearchTerm('');
    setTimeout(() => {
      handleSearch();
    }, 0);
  };
  // User ID 検索フィールドクリアハンドラ
  const handleClearUserIdField = () => {
    setUserIdSearchTerm('');
    setTimeout(() => {
      handleSearch();
    }, 0);
  };
  // ロール選択ハンドラ
  const handleRoleChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedRoles(value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  // 退会状態表示切替ハンドラ
  const handleResignedStatusChange = (event: SelectChangeEvent) => {
    setResignedStatus(event.target.value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  // 申込み状態表示切替ハンドラ
  const handleAssignStatusChange = (event: SelectChangeEvent) => {
    setAssignStatus(event.target.value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  // 対話状態表示切替ハンドラ
  const handleTalkStatusChange = (event: SelectChangeEvent) => {
    setTalkStatus(event.target.value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  // ブロック状態表示切替ハンドラ
  const handleBlockedStatusChange = (event: SelectChangeEvent) => {
    setBlockedStatus(event.target.value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  // 研修ステータス表示切替ハンドラ
  const handleWorkshopStatusChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedWorkshopStatuses(value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  // タブ切り替えハンドラ
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // 検索条件をクリア
    setSearchTerm('');
    setEmailSearchTerm('');
    setUserIdSearchTerm('');
    setResignedStatus(ResignedStatus.ACTIVE_ONLY);
    setAssignStatus(AssignStatus.ALL);
    setTalkStatus(TalkStatus.ALL);
    setBlockedStatus(BlockedStatus.ALL);
    setSelectedWorkshopStatuses([]);
    setWorkshopFinishCount('');
    setWorkshopFinishCountOperator('eq');
    setConversationCount('');
    setConversationCountOperator('eq');
    setSeniorTalkRoomStatus(SeniorTalkRoomStatus.ALL);
    setTestAccountStatus(TestAccountStatus.ALL);
    setCreatedAtStart(null);
    setCreatedAtEnd(null);
    // 研修看護師タブの場合（値=1）は、ロールを"研修看護師"に設定
    if (newValue === 1) {
      setSelectedRoles([ 'STAFF' ]); // STAFFは研修看護師を意味する
      // 検索条件を更新
      setSearchParams({
        searchTerm: '',
        emailSearchTerm: '',
        userIdSearchTerm: '',
        selectedRoles: [ 'STAFF' ], // 研修看護師をセット
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        assignStatus: AssignStatus.ALL,
        talkStatus: TalkStatus.ALL,
        blockedStatus: BlockedStatus.ALL,
        selectedWorkshopStatuses: [],
        workshopFinishCount: '',
        workshopFinishCountOperator: 'eq',
        conversationCount: '',
        conversationCountOperator: 'eq',
        seniorTalkRoomStatus: SeniorTalkRoomStatus.ALL,
        testAccountStatus: TestAccountStatus.ALL,
        createdAtStart: null,
        createdAtEnd: null
      });
    } else {
      // 通常看護師タブの場合（値=0）は、ロールもリセット
      setSelectedRoles([]);
      // 検索条件を更新
      setSearchParams({
        searchTerm: '',
        emailSearchTerm: '',
        userIdSearchTerm: '',
        selectedRoles: [],
        resignedStatus: ResignedStatus.ACTIVE_ONLY,
        assignStatus: AssignStatus.ALL,
        talkStatus: TalkStatus.ALL,
        blockedStatus: BlockedStatus.ALL,
        selectedWorkshopStatuses: [],
        workshopFinishCount: '',
        workshopFinishCountOperator: 'eq',
        conversationCount: '',
        conversationCountOperator: 'eq',
        seniorTalkRoomStatus: SeniorTalkRoomStatus.ALL,
        testAccountStatus: TestAccountStatus.ALL,
        createdAtStart: null,
        createdAtEnd: null
      });
    }
    // ページもリセット
    setPage(0);
  };
  // 研修修了回数の入力ハンドラ
  const handleWorkshopFinishCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 数値のみ許可（空も可）
    const value = event.target.value;
    if (value === '' || /^[0-9]+$/.test(value)) {
      setWorkshopFinishCount(value);
    }
  };
  // 研修修了回数の比較演算子変更ハンドラ
  const handleWorkshopFinishCountOperatorChange = (event: SelectChangeEvent) => {
    setWorkshopFinishCountOperator(event.target.value);
  };
  // 対話回数の入力ハンドラ
  const handleConversationCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 数値のみ許可（空も可）
    const value = event.target.value;
    if (value === '' || /^[0-9]+$/.test(value)) {
      setConversationCount(value);
    }
  };
  // 対話回数の比較演算子変更ハンドラ
  const handleConversationCountOperatorChange = (event: SelectChangeEvent) => {
    setConversationCountOperator(event.target.value);
  };
  // 検索実行関数
  const handleSearch = () => {
    console.log('検索条件：', {
      searchTerm,
      emailSearchTerm,
      userIdSearchTerm,
      selectedRoles,
      tabValue,
      talkStatus,
      assignStatus,
      selectedWorkshopStatuses,
      workshopFinishCount,
      workshopFinishCountOperator,
      conversationCount,
      conversationCountOperator,
      resignedStatus,
      createdAtStart,
      createdAtEnd
    });
    // 検索条件を更新
    setSearchParams({
      searchTerm,
      emailSearchTerm,
      userIdSearchTerm,
      selectedRoles,
      talkStatus,
      assignStatus,
      blockedStatus,
      selectedWorkshopStatuses,
      workshopFinishCount,
      workshopFinishCountOperator,
      conversationCount,
      conversationCountOperator,
      seniorTalkRoomStatus,
      resignedStatus,
      testAccountStatus,
      createdAtStart,
      createdAtEnd
    });
    // ページをリセット
    setPage(0);
  };
  // CSV出力ハンドラ
  const handleCSVExport = async() => {
    try {
      // 一覧表示と同じAPIパラメータ形式を作成
      const params: NurseSearchParams = {
        search: searchParams.searchTerm || undefined,
        email_search: searchParams.emailSearchTerm || undefined,
        user_id: searchParams.userIdSearchTerm || undefined,
        is_training_mode: trainingMode,
      };
      // ロールフィルターの追加
      if (searchParams.selectedRoles && searchParams.selectedRoles.length > 0) {
        params.role_type = searchParams.selectedRoles;
      }
      // 申込み状態フィルターの追加
      if (searchParams.assignStatus) {
        if (searchParams.assignStatus === AssignStatus.ASSIGNABLE) {
          params.can_assign = 'true';
        } else if (searchParams.assignStatus === AssignStatus.UNASSIGNABLE) {
          params.can_assign = 'false';
        }
      }
      // 対話状態フィルターの追加
      if (searchParams.talkStatus && searchParams.talkStatus !== TalkStatus.ALL) {
        params.talk_status = searchParams.talkStatus;
      }
      // ブロック状態フィルターの追加
      if (searchParams.blockedStatus && searchParams.blockedStatus !== BlockedStatus.ALL) {
        params.blocked_status = searchParams.blockedStatus;
      }
      // 研修ステータスフィルターの追加
      if (Array.isArray(searchParams.selectedWorkshopStatuses) && searchParams.selectedWorkshopStatuses.length > 0) {
        params.workshop_status = searchParams.selectedWorkshopStatuses;
      }
      // 研修修了回数フィルターの追加
      if (searchParams.workshopFinishCount && searchParams.workshopFinishCount.trim() !== '') {
        const count = searchParams.workshopFinishCount.trim();
        if (searchParams.workshopFinishCountOperator === 'eq') {
          params.workshop_finish_count = count;
        } else if (searchParams.workshopFinishCountOperator === 'gte') {
          params.workshop_finish_count_gte = count;
        } else if (searchParams.workshopFinishCountOperator === 'lte') {
          params.workshop_finish_count_lte = count;
        }
      }
      // 対話回数フィルターの追加
      if (searchParams.conversationCount && searchParams.conversationCount.trim() !== '') {
        const count = searchParams.conversationCount.trim();
        if (searchParams.conversationCountOperator === 'eq') {
          params.conversation_count = count;
        } else if (searchParams.conversationCountOperator === 'gte') {
          params.conversation_count_gte = count;
        } else if (searchParams.conversationCountOperator === 'lte') {
          params.conversation_count_lte = count;
        }
      }
      // 上席相談ルーム関係フィルターの追加
      if (searchParams.seniorTalkRoomStatus && searchParams.seniorTalkRoomStatus !== SeniorTalkRoomStatus.ALL) {
        params.senior_talk_room_status = searchParams.seniorTalkRoomStatus;
      }
      // 登録日範囲フィルターの追加
      if (searchParams.createdAtStart) {
        params.created_at_start = searchParams.createdAtStart;
        if (searchParams.createdAtEnd) {
          params.created_at_end = searchParams.createdAtEnd;
        }
      }
      // 退会状態フィルターの追加
      if (searchParams.resignedStatus) {
        params.include_resigned = searchParams.resignedStatus;
      }
      // テストアカウントフィルターの追加
      if (searchParams.testAccountStatus && searchParams.testAccountStatus !== TestAccountStatus.ALL) {
        params.is_test_account = searchParams.testAccountStatus;
      }
      // プレビューデータを取得
      const preview = await getNursesCSVPreview(params);
      setCSVPreviewData(preview);
      setCSVDialogOpen(true);
    } catch (error) {
      console.error('CSV preview error:', error);
    }
  };
  // CSV出力実行
  const handleCSVExportConfirm = async(selectedOptions: string[], excludeFields: string[]) => {
    try {
      // 一覧表示と同じAPIパラメータ形式を作成（プレビューと同じ）
      const params: CSVExportParams = {
        search: searchParams.searchTerm || undefined,
        email_search: searchParams.emailSearchTerm || undefined,
        user_id: searchParams.userIdSearchTerm || undefined,
        is_training_mode: trainingMode,
        exclude_fields: excludeFields,
      };
      // ロールフィルターの追加
      if (searchParams.selectedRoles && searchParams.selectedRoles.length > 0) {
        params.role_type = searchParams.selectedRoles;
      }
      // 申込み状態フィルターの追加
      if (searchParams.assignStatus) {
        if (searchParams.assignStatus === AssignStatus.ASSIGNABLE) {
          params.can_assign = 'true';
        } else if (searchParams.assignStatus === AssignStatus.UNASSIGNABLE) {
          params.can_assign = 'false';
        }
      }
      // 対話状態フィルターの追加
      if (searchParams.talkStatus && searchParams.talkStatus !== TalkStatus.ALL) {
        params.talk_status = searchParams.talkStatus;
      }
      // ブロック状態フィルターの追加
      if (searchParams.blockedStatus && searchParams.blockedStatus !== BlockedStatus.ALL) {
        params.blocked_status = searchParams.blockedStatus;
      }
      // 研修ステータスフィルターの追加
      if (Array.isArray(searchParams.selectedWorkshopStatuses) && searchParams.selectedWorkshopStatuses.length > 0) {
        params.workshop_status = searchParams.selectedWorkshopStatuses;
      }
      // 研修修了回数フィルターの追加
      if (searchParams.workshopFinishCount && searchParams.workshopFinishCount.trim() !== '') {
        const count = searchParams.workshopFinishCount.trim();
        if (searchParams.workshopFinishCountOperator === 'eq') {
          params.workshop_finish_count = count;
        } else if (searchParams.workshopFinishCountOperator === 'gte') {
          params.workshop_finish_count_gte = count;
        } else if (searchParams.workshopFinishCountOperator === 'lte') {
          params.workshop_finish_count_lte = count;
        }
      }
      // 対話回数フィルターの追加
      if (searchParams.conversationCount && searchParams.conversationCount.trim() !== '') {
        const count = searchParams.conversationCount.trim();
        if (searchParams.conversationCountOperator === 'eq') {
          params.conversation_count = count;
        } else if (searchParams.conversationCountOperator === 'gte') {
          params.conversation_count_gte = count;
        } else if (searchParams.conversationCountOperator === 'lte') {
          params.conversation_count_lte = count;
        }
      }
      // 上席相談ルーム関係フィルターの追加
      if (searchParams.seniorTalkRoomStatus && searchParams.seniorTalkRoomStatus !== SeniorTalkRoomStatus.ALL) {
        params.senior_talk_room_status = searchParams.seniorTalkRoomStatus;
      }
      // 登録日範囲フィルターの追加
      if (searchParams.createdAtStart) {
        params.created_at_start = searchParams.createdAtStart;
        if (searchParams.createdAtEnd) {
          params.created_at_end = searchParams.createdAtEnd;
        }
      }
      // 退会状態フィルターの追加
      if (searchParams.resignedStatus) {
        params.include_resigned = searchParams.resignedStatus;
      }
      // テストアカウントフィルターの追加
      if (searchParams.testAccountStatus && searchParams.testAccountStatus !== TestAccountStatus.ALL) {
        params.is_test_account = searchParams.testAccountStatus;
      }
      await exportCSV(params);
    } catch (error) {
      // エラーはuseCSVExport内で処理される
    }
  };
  // フィルターをクリア
  const handleClearFilters = () => {
    // すべての検索フィルターをリセット
    setSearchTerm('');
    setEmailSearchTerm('');
    setUserIdSearchTerm('');
    // 現在のタブモードに応じてロールを設定
    const resetRoles = tabValue === 1 ? [ 'STAFF' ] : [];
    setSelectedRoles(resetRoles);
    setResignedStatus(ResignedStatus.ACTIVE_ONLY);
    setAssignStatus(AssignStatus.ALL);
    setTalkStatus(TalkStatus.ALL);
    setBlockedStatus(BlockedStatus.ALL);
    setSelectedWorkshopStatuses([]);
    setWorkshopFinishCount('');
    setWorkshopFinishCountOperator('eq');
    setConversationCount('');
    setConversationCountOperator('eq');
    setSeniorTalkRoomStatus(SeniorTalkRoomStatus.ALL);
    setTestAccountStatus(TestAccountStatus.ALL);
    setCreatedAtStart(null);
    setCreatedAtEnd(null);
    // タブは現在のモードを維持
    // 検索条件を更新
    setSearchParams({
      searchTerm: '',
      emailSearchTerm: '',
      userIdSearchTerm: '',
      selectedRoles: resetRoles,
      talkStatus: TalkStatus.ALL,
      assignStatus: AssignStatus.ALL,
      blockedStatus: BlockedStatus.ALL,
      selectedWorkshopStatuses: [],
      workshopFinishCount: '',
      workshopFinishCountOperator: 'eq',
      conversationCount: '',
      conversationCountOperator: 'eq',
      seniorTalkRoomStatus: SeniorTalkRoomStatus.ALL,
      resignedStatus: ResignedStatus.ACTIVE_ONLY,
      testAccountStatus: TestAccountStatus.ALL,
      createdAtStart: null,
      createdAtEnd: null
    });
    // ページをリセット
    setPage(0);
  };
  // シニア対話ルーム状態変更ハンドラ
  const handleSeniorTalkRoomStatusChange = (event: SelectChangeEvent) => {
    setSeniorTalkRoomStatus(event.target.value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  // テストアカウント状態変更ハンドラ
  const handleTestAccountStatusChange = (event: SelectChangeEvent) => {
    setTestAccountStatus(event.target.value);
    // 検索は即時実行しない（検索ボタンのクリック時に実行）
  };
  return (
    <Box>
      {/* モード切替タブ - 検索セクションの上に配置 */}
      <Box sx={{ borderBottom: 0, borderColor: 'divider', mb: 0 }}>
        <ModeTabs value={tabValue} onChange={handleTabChange}/>
      </Box>

      {/* 検索フォーム */}
      <NurseSearchForm
        trainingMode={trainingMode}
        // 共通フィールド
        searchTerm={searchTerm}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
        onClearSearchField={handleClearSearchField}
        emailSearchTerm={emailSearchTerm}
        onEmailSearchTermChange={(e) => setEmailSearchTerm(e.target.value)}
        onClearEmailField={handleClearEmailField}
        userIdSearchTerm={userIdSearchTerm}
        onUserIdSearchTermChange={(e) => setUserIdSearchTerm(e.target.value)}
        onClearUserIdField={handleClearUserIdField}
        selectedRoles={selectedRoles}
        onRoleChange={handleRoleChange}

        // 通常モードフィールド
        talkStatus={talkStatus}
        onTalkStatusChange={handleTalkStatusChange}
        assignStatus={assignStatus}
        onAssignStatusChange={handleAssignStatusChange}
        blockedStatus={blockedStatus}
        onBlockedStatusChange={handleBlockedStatusChange}
        conversationCount={conversationCount}
        onConversationCountChange={handleConversationCountChange}
        conversationCountOperator={conversationCountOperator}
        onConversationCountOperatorChange={handleConversationCountOperatorChange}
        seniorTalkRoomStatus={seniorTalkRoomStatus}
        onSeniorTalkRoomStatusChange={handleSeniorTalkRoomStatusChange}

        // 研修モードフィールド
        selectedWorkshopStatuses={selectedWorkshopStatuses}
        onWorkshopStatusChange={handleWorkshopStatusChange}
        workshopFinishCount={workshopFinishCount}
        onWorkshopFinishCountChange={handleWorkshopFinishCountChange}
        workshopFinishCountOperator={workshopFinishCountOperator}
        onWorkshopFinishCountOperatorChange={handleWorkshopFinishCountOperatorChange}

        // 共有フィールド
        resignedStatus={resignedStatus}
        onResignedStatusChange={handleResignedStatusChange}
        testAccountStatus={testAccountStatus}
        onTestAccountStatusChange={handleTestAccountStatusChange}

        // 登録日範囲検索フィールド
        createdAtStart={createdAtStart}
        onCreatedAtStartChange={setCreatedAtStart}
        createdAtEnd={createdAtEnd}
        onCreatedAtEndChange={setCreatedAtEnd}

        // 検索操作
        totalCount={totalCount}
        onSearch={handleSubmit}
        onClear={handleClearFilters}
        onCSVExport={user?.role === 'ADMIN' ? handleCSVExport : undefined}
      />

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 看護師一覧テーブル */}
      <NurseTable
        nurses={nurses}
        trainingMode={trainingMode}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        searchParams={searchParams}
      />

      {/* CSV出力ダイアログ */}
      <CSVExportDialog
        open={csvDialogOpen}
        onClose={() => setCSVDialogOpen(false)}
        title="看護師情報 ダウンロード"
        onExport={handleCSVExportConfirm}
        previewData={csvPreviewData ? {
          headers: csvPreviewData.headers,
          all_headers: csvPreviewData.all_headers,
          personal_fields: csvPreviewData.personal_fields,
          rows: csvPreviewData.rows,
          totalCount: csvPreviewData.total_count
        } : undefined}
        loading={csvLoading}
        error={csvError}
      />
    </Box>
  );
};
export default NurseList;
